"use client";

import { c as _c } from "react/compiler-runtime";
import { Bot, CloudSun, Code2, Copy, Check, FileText, Mail, ScanText, SmilePlus, UserSearch, Wrench, Zap } from "lucide-react";
import { useState, useCallback } from "react";
import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import { Card } from "@/lib/components/card";
import { useI18n } from "@llmgateway/i18n/react";
interface Agent {
  nameKey: string;
  descriptionKey: string;
  templateId: string;
  icon: typeof Bot;
  capabilityKeys: string[];
  tags: string[];
  gradient: string;
  featured?: boolean;
}
const agents: Agent[] = [{
  nameKey: "public.agents.items.weather.name",
  descriptionKey: "public.agents.items.weather.description",
  templateId: "weather-agent",
  icon: CloudSun,
  capabilityKeys: ["public.agents.items.weather.capabilityToolCalling", "public.agents.items.weather.capabilityRealTimeData", "public.agents.items.weather.capabilityNaturalLanguage"],
  tags: ["TypeScript", "AI SDK", "OpenAI"],
  gradient: "from-sky-500/20 via-cyan-500/20 to-teal-500/20",
  featured: true
}, {
  nameKey: "public.agents.items.lead.name",
  descriptionKey: "public.agents.items.lead.description",
  templateId: "lead-agent",
  icon: UserSearch,
  capabilityKeys: ["public.agents.items.lead.capabilityWebSearch", "public.agents.items.lead.capabilityProfileResearch", "public.agents.items.lead.capabilityWebhookDelivery"],
  tags: ["TypeScript", "AI SDK", "Perplexity"],
  gradient: "from-violet-500/20 via-purple-500/20 to-fuchsia-500/20"
}, {
  nameKey: "public.agents.items.changelog.name",
  descriptionKey: "public.agents.items.changelog.description",
  templateId: "changelog-generator-agent",
  icon: FileText,
  capabilityKeys: ["public.agents.items.weather.capabilityToolCalling", "public.agents.items.changelog.capabilityGitAnalysis", "public.agents.items.changelog.capabilityStructuredOutput"],
  tags: ["TypeScript", "AI SDK", "Zod"],
  gradient: "from-amber-500/20 via-orange-500/20 to-red-500/20"
}, {
  nameKey: "public.agents.items.email.name",
  descriptionKey: "public.agents.items.email.description",
  templateId: "email-drafter-agent",
  icon: Mail,
  capabilityKeys: ["public.agents.items.changelog.capabilityStructuredOutput", "public.agents.items.email.capabilityToneControl", "public.agents.items.email.capabilityTextGeneration"],
  tags: ["TypeScript", "AI SDK", "Zod"],
  gradient: "from-blue-500/20 via-indigo-500/20 to-violet-500/20"
}, {
  nameKey: "public.agents.items.sentiment.name",
  descriptionKey: "public.agents.items.sentiment.description",
  templateId: "sentiment-analyzer-agent",
  icon: SmilePlus,
  capabilityKeys: ["public.agents.items.sentiment.capabilitySentimentAnalysis", "public.agents.items.sentiment.capabilityKeyPhrases", "public.agents.items.sentiment.capabilityFileInput"],
  tags: ["TypeScript", "AI SDK", "Zod"],
  gradient: "from-emerald-500/20 via-green-500/20 to-teal-500/20"
}, {
  nameKey: "public.agents.items.extractor.name",
  descriptionKey: "public.agents.items.extractor.description",
  templateId: "data-extractor-agent",
  icon: ScanText,
  capabilityKeys: ["public.agents.items.extractor.capabilityEntityExtraction", "public.agents.items.changelog.capabilityStructuredOutput", "public.agents.items.extractor.capabilityNlp"],
  tags: ["TypeScript", "AI SDK", "Zod"],
  gradient: "from-rose-500/20 via-pink-500/20 to-fuchsia-500/20"
}];
export function AgentCards() {
  const $ = _c(24);
  const {
    t
  } = useI18n();
  const [copiedTemplateId, setCopiedTemplateId] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = templateId => {
      navigator.clipboard.writeText(`npx @llmgateway/cli init --template ${templateId}`);
      setCopiedTemplateId(templateId);
      setTimeout(() => setCopiedTemplateId(null), 2000);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const copyToClipboard = t0;
  if (agents.length === 0) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Bot className="mx-auto h-12 w-12 text-muted-foreground/50" />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t) {
      t2 = t("public.agents.empty");
      $[2] = t;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t2) {
      t3 = <div className="text-center py-16">{t1}<p className="mt-4 text-muted-foreground">{t2}</p></div>;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t1;
  if ($[6] !== copiedTemplateId || $[7] !== t) {
    t1 = agents.map(agent => <Card key={agent.nameKey} className="group relative overflow-hidden border-0 bg-gradient-to-br from-background to-muted/30 shadow-xl hover:shadow-2xl transition-all duration-500"><div className={`absolute inset-0 bg-gradient-to-br ${agent.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />{agent.featured && <div className="absolute top-4 right-4 z-10"><Badge className="bg-gradient-to-r from-sky-600 to-cyan-600 text-white border-0 shadow-lg"><Zap className="mr-1 h-3 w-3" />{t("public.common.featured")}</Badge></div>}<div className="relative p-6 space-y-6"><div className="relative"><div className="absolute inset-0 bg-gradient-to-br from-sky-500 to-cyan-500 rounded-2xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500" /><div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-500 shadow-lg"><agent.icon className="h-8 w-8 text-white" /></div></div><div className="space-y-3"><h3 className="text-2xl font-bold tracking-tight">{t(agent.nameKey)}</h3><p className="text-muted-foreground leading-relaxed">{t(agent.descriptionKey)}</p></div><div className="space-y-2"><div className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><Wrench className="h-4 w-4" />{t("public.agents.capabilitiesLabel")}</div><div className="flex flex-wrap gap-2">{agent.capabilityKeys.map(capabilityKey => <Badge key={capabilityKey} variant="outline" className="bg-background/50 border-sky-500/30 text-sky-600 dark:text-sky-400">{t(capabilityKey)}</Badge>)}</div></div><div className="flex flex-wrap gap-2">{agent.tags.map(_temp)}</div><div className="flex pt-2"><Button variant="outline" className="w-full gap-2" onClick={() => copyToClipboard(agent.templateId)}>{copiedTemplateId === agent.templateId ? <><Check className="h-4 w-4 text-green-500" />{t("public.common.copied")}</> : <><Copy className="h-4 w-4" />{t("public.common.clone")}</>}</Button></div></div><div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-500 via-cyan-500 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500" /></Card>);
    $[6] = copiedTemplateId;
    $[7] = t;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted"><Bot className="h-8 w-8 text-muted-foreground" /></div>;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t) {
    t3 = t("public.agents.moreTitle");
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t3) {
    t4 = <h3 className="text-xl font-semibold text-muted-foreground">{t3}</h3>;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t) {
    t5 = t("public.agents.moreDescription");
    $[14] = t;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t5) {
    t6 = <p className="text-sm text-muted-foreground/80 max-w-xs">{t5}</p>;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t6) {
    t7 = <Card className="relative overflow-hidden border-dashed border-2 bg-muted/20 hover:bg-muted/30 transition-colors duration-300"><div className="p-6 space-y-6 flex flex-col items-center justify-center min-h-[400px] text-center">{t2}<div className="space-y-2">{t4}{t6}</div></div></Card>;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t1 || $[22] !== t7) {
    t8 = <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-2 max-w-5xl mx-auto">{t1}{t7}</div>;
    $[21] = t1;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
function _temp(tag) {
  return <Badge key={tag} variant="secondary" className="bg-muted/80 hover:bg-muted transition-colors"><Code2 className="mr-1 h-3 w-3" />{tag}</Badge>;
}